import { createLogger } from "@/logger";

const log = createLogger("feed");

import { getFeedItemContent, getWorkspaceFeedPermissions } from "@storyboard/web-client";
import { transport } from "@/config";
import { bulkUpsertWsAudioEncodings } from "@/models/repositories/AudioEncoding";
import { bulkUpsertCallRecord } from "@/models/repositories/CallRecord";
import { bulkUpsertDisplayArtifact } from "@/models/repositories/DisplayArtifact";
import { markPermissionsLoaded } from "@/models/repositories/Feed";
import { bulkUpsertFile } from "@/models/repositories/File";
import { markItemsContentLoaded } from "@/models/repositories/Item";
import { bulkUpsertLink } from "@/models/repositories/Link";
import { bulkUpsertPermission } from "@/models/repositories/Permission";
import { idx, rowsByIndex, store } from "@/store/store";
import { languageFamily, type PreferredLanguage } from "../languages";
import { bulkUpsertWsTranscriptions } from "../repositories";
import { downloadOldestFeedItemEvents } from "./bootstrap";

export async function downloadPaginatedFeedPermissions({
  workspaceId,
  feedId,
}: {
  workspaceId: string;
  feedId: string;
}): Promise<void> {
  let pageSize = 50000;
  let page = 0;
  while (true) {
    const response = await getWorkspaceFeedPermissions(transport, {
      workspaceId,
      feedId,
      page,
      pageSize,
    });
    if (response.pageSize < pageSize) {
      pageSize = response.pageSize;
    }
    await bulkUpsertPermission(response.permissions);
    if (response.permissions.length === 0 || response.permissions.length < response.pageSize || page > 1000) {
      break;
    }
    page += 1;
  }
  await markPermissionsLoaded(feedId);
}

export async function downloadFeedItemContent(
  workspaceId: string,
  feedItemIds: string[],
  language?: PreferredLanguage,
  timestamp?: string,
) {
  if (feedItemIds.length === 0) return;

  log.info("[DOWNLOAD CONTENT] Starting download", {
    itemCount: feedItemIds.length,
    itemIds: feedItemIds,
    language,
  });

  const chunkSize = 100;
  const itemIdChunks: string[][] = [];
  for (let i = 0; i < feedItemIds.length; i += chunkSize) {
    itemIdChunks.push(feedItemIds.slice(i, i + chunkSize));
  }

  const eventPromises = itemIdChunks.map((itemChunk) =>
    downloadOldestFeedItemEvents(itemChunk, workspaceId, timestamp, true, 2),
  );

  // The batch wraps each chunk's writes, never its fetch: an open batch suppresses every
  // `subscribeWrites` notification in the app (the snapshot scheduler, `useCollectionsCompute`,
  // `useCollectionRow`), so holding one across these round trips froze unrelated live updates for
  // the whole download (WEB-2237). Per-chunk batches still coalesce each response into one
  // notification while later chunks are in flight.
  // The endpoint matches a language exactly, so a reader of a language with dialects asks for each of them, and for
  // the original (`none`), which the selection falls back to when the message has no translation for the reader.
  const requestLanguages: (PreferredLanguage | undefined)[] =
    language === undefined || language === "none" ? [language] : [...languageFamily(language), "none"];

  await Promise.all(
    itemIdChunks.map(async (itemChunk) => {
      const responses = await Promise.all(
        requestLanguages.map((requestLanguage) =>
          getFeedItemContent(transport, {
            workspaceId,
            itemIds: itemChunk,
            preferredLanguage: requestLanguage,
            timestamp,
          }),
        ),
      );
      await store.batch(async () => {
        for (const response of responses) {
          await bulkUpsertWsTranscriptions(response?.transcriptions || []);
          await bulkUpsertWsAudioEncodings(response?.audioEncodings || []);
          await bulkUpsertFile(response?.files || []);
          await bulkUpsertLink(response?.links || []);
          await bulkUpsertDisplayArtifact(response?.displayArtifacts || []);
          await bulkUpsertCallRecord(response?.callRecords || []);
        }
      });
    }),
  );

  await markItemsContentLoaded(feedItemIds);

  log.info("[DOWNLOAD CONTENT] Content loaded", { itemCount: feedItemIds.length });

  void Promise.all(eventPromises)
    .then(() => {
      log.info("[DOWNLOAD CONTENT] Events download complete", { itemCount: feedItemIds.length });
    })
    .catch((error) => {
      log.error("[DOWNLOAD CONTENT] Events download failed", { error });
    });
}

/** Stamps `isFromBot` on items authored by bot accounts (set-only). */
export async function initializeFromBot(): Promise<void> {
  store.updateMany("item", store.getAll("item").keys(), (row) => {
    if (row.isFromBot === true || row.accountId == null) return row;
    return store.get("account", row.accountId)?.bot === true ? { ...row, isFromBot: true } : row;
  });
}

/** Raises each feed's `latestActivity` to its newest item's createdAt (GREATEST semantics). */
export async function initializeFeedLatestActivity(): Promise<void> {
  store.updateMany("feed", store.getAll("feed").keys(), (row) => {
    let latest: string | null = null;
    for (const item of rowsByIndex("item", idx.itemsByFeed, row.id)) {
      if (latest === null || item.createdAt > latest) latest = item.createdAt;
    }
    if (latest === null) return row;
    const existing = row.latestActivity === "-infinity" ? null : row.latestActivity;
    return existing == null || latest > existing ? { ...row, latestActivity: latest } : row;
  });
}
